import supabase from "../supabase-client";
import { formatPrice } from "./funcs";

// Egresos: every outgoing-money case that is not a workshop service.
// Schema and rules: supabase/migrations/20261015_egresos.sql. Every balance is
// derived in SQL (egreso_balances, egreso_cost_lines, egreso_money_lines); this
// file only labels and fetches — it never adds money up on its own.

export const KINDS = [
  { value: "compra", label: "Compra", help: "Cotización con anticipo y resto contra entrega (muebles, equipo…)" },
  { value: "cuenta_corriente", label: "Cuenta corriente", help: "Proveedor que da crédito: facturas que se pagan cuando se puede" },
  { value: "subarrendamiento", label: "Subarrendamiento", help: "Renta de un auto a otra arrendadora, con depósito que regresa" },
  { value: "anticipo", label: "Anticipo a comprobar", help: "Dinero que se le da a alguien para que compre algo y regrese ticket y cambio" },
  { value: "reembolso", label: "Reembolso", help: "Alguien pagó algo por nosotros y se le regresa (verificación, refacción…)" },
  { value: "otro", label: "Otro", help: "" },
];
export const KIND_LABEL = Object.fromEntries(KINDS.map((k) => [k.value, k.label]));

export const PAYEE_TYPE_LABEL = {
  service_provider: "Proveedor",
  customer: "Cliente",
  company: "Empresa",
  trasladista: "Trasladista",
  payee: "Tercero",
};

export const ENTRY_LABEL = {
  cost: "Comprobante",
  paid_out: "Pago",
  returned_in: "Nos regresó",
  deposit_out: "Depósito entregado",
  deposit_in: "Depósito devuelto",
  writeoff: "Pérdida",
};

export const METHODS = [
  { value: "spei", label: "SPEI / transferencia" },
  { value: "efectivo", label: "Efectivo" },
  { value: "clara", label: "Tarjeta Clara" },
  { value: "tarjeta", label: "Otra tarjeta" },
  { value: "otro", label: "Otro" },
];
export const METHOD_LABEL = Object.fromEntries(METHODS.map((m) => [m.value, m.label]));

// Egresos that never take an invoice: the proof is the payment receipt. A card
// payment is not an expense (each card charge carries its own CFDI).
export const NO_INVOICE_REASONS = [
  { value: "pago_tarjeta", label: "Pago de tarjeta de crédito" },
  { value: "impuestos", label: "Impuestos / SAT" },
  { value: "prestamo", label: "Pago de préstamo o crédito" },
  { value: "traspaso", label: "Traspaso a cuenta propia" },
  { value: "no_deducible", label: "Gasto no deducible" },
  { value: "otro", label: "Otro" },
];
export const NO_INVOICE_LABEL = Object.fromEntries(NO_INVOICE_REASONS.map((r) => [r.value, r.label]));
// For a Select: nothing chosen until the person picks a motivo.
export const NO_INVOICE_OPTIONS = [{ label: "Elige un motivo", value: "", disabled: true }, ...NO_INVOICE_REASONS];

export const TRIGGER_LABEL = { al_pedir: "Al pedir", contra_entrega: "Contra entrega", fecha: "En fecha" };

export const DEDUCTIBLE_TONE = { deducible: "success", no_deducible: "critical", pendiente: "attention" };
export const DEDUCTIBLE_LABEL = { deducible: "Deducible", no_deducible: "No deducible", pendiente: "Pendiente" };

export const money = (cents) => formatPrice(Number(cents || 0) / 100);
export const toCents = (pesos) => {
  const n = Number(String(pesos ?? "").replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
};

export const DOCS_BUCKET = "egresos-docs";

// Where a payee's profile lives in the app.
export const payeeUrl = (type, id) =>
  ({
    service_provider: `/service_providers/${id}`,
    customer: `/customers/edit?id=${id}`,
    company: `/companies/edit?id=${id}`,
    trasladista: `/trasladistas`,
    payee: null,
  })[type];

// The single status line for a case: what needs doing next, or "Al corriente".
// A payment request waits for whoever can pay (approval: pendiente/rechazada).
export function egresoStatus(b, closedAt, approval) {
  if (approval === "rechazada") return { tone: "critical", label: "Rechazada" };
  if (closedAt) return { tone: undefined, label: "Cerrado" };
  if (approval === "pendiente") return { tone: "attention", label: "Solicitada" };
  if (!b) return { tone: undefined, label: "—" };
  if (b.por_comprobar_cents > 0) return { tone: "warning", label: "Por comprobar" };
  if (b.por_pagar_cents > 0) return { tone: "attention", label: "Por pagar" };
  if (b.deposito_cents > 0) return { tone: "info", label: "Depósito por recuperar" };
  return { tone: "success", label: "Al corriente" };
}

export async function uploadEgresoDoc(egresoId, kind, file) {
  const ext = (file.name.split(".").pop() || "bin").toLowerCase();
  const path = `${egresoId}/${kind}_${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from(DOCS_BUCKET).upload(path, file, { upsert: false });
  if (error) throw error;
  return path;
}

export async function openEgresoDoc(path) {
  const { data, error } = await supabase.storage.from(DOCS_BUCKET).createSignedUrl(path, 1200);
  if (error) throw error;
  window.open(data.signedUrl, "_blank", "noopener");
}

// Supabase RPC errors carry the Spanish message raised in SQL.
export const rpcError = (error) => error?.message?.replace(/^.*?ERROR:\s*/, "") || "Algo salió mal";

// PostgREST filters are parsed as a little language: keep search text inert.
const safeLike = (s) => s.replace(/[,()%*\\]/g, " ").trim();

// Search every place a payee can live. Returns [{payee_type, payee_id, name, detail}].
export async function searchPayees(q) {
  const text = q.trim();
  if (text.length < 2) return [];
  const like = `%${safeLike(text)}%`;
  const [providers, clients, trasladistas, terceros] = await Promise.all([
    supabase.rpc("search_service_providers", { search_text: text }),
    supabase.rpc("search_clients", { search_text: text }),
    supabase.from("trasladistas").select("id, name, rfc").ilike("name", like).limit(5),
    supabase
      .from("payees")
      .select("id, name, nickname, rfc, phone, companies(name)")
      .or(`name.ilike.${like},nickname.ilike.${like},rfc.ilike.${like},phone.ilike.${like}`)
      .limit(8),
  ]);
  const out = [];
  for (const p of terceros.data || [])
    out.push({
      payee_type: "payee",
      payee_id: p.id,
      name: p.nickname || p.name,
      detail: [p.nickname && p.name, p.companies?.name && `de ${p.companies.name}`, p.rfc, p.phone].filter(Boolean).join(" · "),
    });
  for (const p of (providers.data || []).slice(0, 6))
    out.push({
      payee_type: "service_provider",
      payee_id: p.id,
      name: p.name,
      detail: [p.contact_name, p.city].filter(Boolean).join(" · "),
    });
  for (const c of (clients.data || []).slice(0, 6))
    out.push({
      payee_type: c.client_type === "company" ? "company" : "customer",
      payee_id: c.id,
      name: c.name,
      detail: c.detail || "",
    });
  for (const t of trasladistas.data || [])
    out.push({ payee_type: "trasladista", payee_id: t.id, name: t.name, detail: t.rfc || "" });
  return out;
}

// ---------------------------------------------------------------- formats
// Same rules as the database (payee_format_error / *_is_valid); the form
// checks them as you type, the database is the backstop.
export const clabeIsValid = (c) => {
  if (!/^\d{18}$/.test(c || "")) return false;
  const w = [3, 7, 1];
  let sum = 0;
  for (let i = 0; i < 17; i++) sum += (Number(c[i]) * w[i % 3]) % 10;
  return (10 - (sum % 10)) % 10 === Number(c[17]);
};

// Bank from the CLABE's first 3 digits (ABM code). Display only: Fintoc's
// verification reports the institution authoritatively.
const BANKS = {
  "002": "Banamex", "006": "Bancomext", "012": "BBVA", "014": "Santander", "019": "Banjército",
  "021": "HSBC", "030": "Bajío", "036": "Inbursa", "042": "Mifel", "044": "Scotiabank",
  "058": "Banregio", "059": "Invex", "060": "Bansi", "062": "Afirme", "072": "Banorte",
  "106": "Bank of America", "112": "Bmonex", "113": "Ve por Más", "127": "Azteca", "130": "Compartamos",
  "132": "Multiva", "137": "BanCoppel", "138": "ABC Capital", "140": "Consubanco", "145": "BBase",
  "147": "Bankaool", "148": "PagaTodo", "150": "Inmobiliario", "152": "Bancrea", "155": "ICBC",
  "156": "Sabadell", "166": "Banco del Bienestar", "167": "Hey Banco", "638": "Nu", "646": "STP",
  "652": "Credicapital", "659": "Opciones Empresariales", "684": "Transfer", "699": "Fondeadora",
  "706": "Arcus", "722": "Mercado Pago", "728": "Spin by OXXO", "734": "Finco Pay", "738": "Fintoc",
};
export const bankFromClabe = (c) => (/^\d{3}/.test(c || "") ? BANKS[c.slice(0, 3)] || null : null);

export const PAYEE_FORMAT = {
  rfc: (v) => (!v || /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(v) ? null : "12 caracteres (empresa) o 13 (persona)"),
  curp: (v) => (!v || /^[A-Z]{4}\d{6}[HMX][A-Z]{2}[B-DF-HJ-NP-TV-Z]{3}[A-Z0-9]\d$/.test(v) ? null : "18 caracteres, como en la credencial"),
  clabe: (v) =>
    !v ? "La CLABE es obligatoria" : v.length !== 18 ? "Deben ser 18 dígitos" : clabeIsValid(v) ? null : "El dígito verificador no coincide: revisa la CLABE",
  phone: (v) => (!v || v.replace(/\D/g, "").slice(-10).length === 10 ? null : "10 dígitos"),
  email: (v) => (!v || /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(v.trim()) ? null : "Correo no válido"),
  spei_concept: (v) => (!v || /^[A-Za-z0-9 -]{1,40}$/.test(v.trim()) ? null : "Solo letras sin acentos, números, espacios y guiones (máximo 40)"),
};
export const payeeFormatErrors = (f) =>
  Object.fromEntries(Object.entries(PAYEE_FORMAT).map(([k, check]) => [k, check(f[k])]).filter(([, e]) => e));
